import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import {
  AssignmentOutlined,
  TrendingUpOutlined,
  AccountCircleOutlined,
  MoreHorizOutlined,
  Brightness2,
  Brightness4,
  ExitToApp,
} from "@material-ui/icons";

/*
 * BottomTabBar — native iOS bottom tab bar (design-uplift R2, tranche 1).
 *
 * Replaces the desktop icon-rail (menu.jsx Drawer) on authenticated routes when
 * the NAV_BOTTOM_TABS flag is on. PURELY PRESENTATIONAL: every navigation/theme
 * action is delegated back to MenuView (onSelect === menu's onMenuSelect,
 * onThemeChange === menu's onThemeChange) so the mentorships flag gate,
 * share-earn modal, logout, mixpanel and theme-resolution logic all stay in ONE
 * place and are NOT regressed.
 *
 * All colors reference tokens defined in src/styles/design-tokens-r1.css
 * (no hardcoded hex). Height is HIG's 49pt min + env(safe-area-inset-bottom) so
 * the bar clears the iOS home indicator. Hidden on the full-screen in-quiz /
 * review surfaces (contract Scope: "the tab bar yields on those routes").
 */

// Primary tabs (<=5 with More). Each tab routes to ONE surface; `match` groups
// the sibling rail destinations so the correct tab lights up on those routes.
//
// Icons are ONE coherent family/weight (MUI *Outlined line glyphs) across all four
// tabs (Practice/Progress/Account + the More button below) — the R2 critic flagged
// the prior mix (filled LiveHelp/AccountCircle vs line TrendingUp/MoreHoriz) as
// incoherent. Practice uses a clipboard glyph (AssignmentOutlined) rather than the
// prior LiveHelp "?"-in-tag, which read as Help rather than practice quizzes.
const PRIMARY_TABS = [
  {
    key: "practice",
    label: "Practice",
    route: "/create-quiz",
    Icon: AssignmentOutlined,
    match: ["/create-quiz", "/absite-diagnostic"],
  },
  {
    key: "progress",
    label: "Progress",
    route: "/overall-performance",
    Icon: TrendingUpOutlined,
    match: ["/overall-performance", "/quiz-history"],
  },
  {
    key: "account",
    label: "Account",
    route: "/user-settings",
    Icon: AccountCircleOutlined,
    match: ["/user-settings"],
  },
];

// Sentence-case a shared nav label for the tab / More-sheet presentation WITHOUT
// mutating the source strings — the flag-off desktop rail (menu.jsx) renders the
// same `item.name` values and MUST stay byte-for-byte unchanged (contract
// Invariant 3 + the R2 recapture brief). Known acronym / brand tokens stay literal
// so "ABSITE Diagnostic" -> "ABSITE diagnostic", "QLink" stays "QLink". Kills the
// R1 sentence-case breach the B lens caught (Title-Case menu rows above the
// already-sentence-case "Dark theme" / "Sign out" group).
const KEEP_LITERAL = new Set(["ABSITE", "QLink", "QLearn"]);
export const toSentenceCase = (label = "") =>
  label
    .split(" ")
    .map((word, i) => {
      if (KEEP_LITERAL.has(word)) return word;
      const lower = word.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");

// Routes owned by a primary tab — excluded from the More overflow sheet so a
// destination is never listed twice.
const PRIMARY_ROUTES = new Set(PRIMARY_TABS.map((t) => t.route));

// Full-screen focus surfaces (all rendered by ExamPage). The tab bar must yield
// its space here; the in-quiz pager is R2 tranche 2.
const IMMERSIVE_RE = /^\/(exam|question\/view|qlink\/preview)(\/|$)/;
export const isImmersiveRoute = (pathname = "") => IMMERSIVE_RE.test(pathname);

const matchesTab = (tab, pathname) =>
  tab.match.some((m) => pathname === m || pathname.startsWith(`${m}/`));

// True when `pathname` is an overflow destination OR a child of one, so a nested
// route (e.g. /mentorships/students/:id under the /mentorships More entry) still
// lights up the More tab. Exact equality alone left such deep routes with no
// active tab (P2). Non-path overflow entries like "share-earn" only ever match
// exactly (no real pathname starts with "share-earn/").
const matchesOverflow = (route, pathname) =>
  pathname === route || pathname.startsWith(`${route}/`);

const useStyles = makeStyles(() => ({
  bar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    // Above page content, below MUI modals/drawers (Modal z-index = 1300).
    zIndex: 1200,
    display: "flex",
    alignItems: "stretch",
    backgroundColor: "var(--surface-2)",
    borderTop: "1px solid var(--nav-tab-divider)",
    // Clears the iOS home indicator (contract: safe-area-bottom inset).
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
  },
  tab: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // HIG 49pt min content height => >=44pt tap target.
    minHeight: "var(--nav-tab-h)",
    padding: "6px 2px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: "inherit",
    // Inactive glyph + label; SvgIcon fills currentColor so this cascades.
    color: "var(--nav-tab-inactive)",
    // S4 (R1): sentence-case chrome — labels never uppercase.
    textTransform: "none",
    "-webkit-tap-highlight-color": "transparent",
    // Themed keyboard-focus ring (replaces the UA-default orange the R2 critic
    // caught). Token-only color; inset so it never clips under the bar edge.
    "&:focus-visible": {
      outline: "2px solid var(--focus-ring)",
      outlineOffset: "-2px",
    },
  },
  tabActive: {
    color: "var(--primary)",
  },
  label: {
    fontSize: 11,
    lineHeight: 1.2,
    marginTop: 2,
  },
  sheetPaper: {
    backgroundColor: "var(--surface-2)",
    color: "var(--text-color)",
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
    maxHeight: "80vh",
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
  },
  grabber: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: "var(--nav-tab-inactive)",
    opacity: 0.5,
    margin: "8px auto 4px",
  },
  sheetIcon: {
    color: "var(--text-color)",
    minWidth: 44,
  },
  sheetText: {
    // S4 (R1): sentence-case chrome.
    textTransform: "none",
  },
  sheetRow: {
    minHeight: 48,
  },
}));

export default function BottomTabBar({
  items = [],
  currentRoute = "",
  onSelect,
  currentTheme = "light",
  onThemeChange,
}) {
  const classes = useStyles();
  const [moreOpen, setMoreOpen] = useState(false);
  const immersive = isImmersiveRoute(currentRoute);

  // Reserve bottom space on the page containers ONLY while the bar is visible
  // (see body.qln-bottom-tabs rule in App.scss). Removed on immersive routes and
  // on unmount so full-screen surfaces are never padded.
  useEffect(() => {
    if (immersive) {
      document.body.classList.remove("qln-bottom-tabs");
      return undefined;
    }
    document.body.classList.add("qln-bottom-tabs");
    return () => document.body.classList.remove("qln-bottom-tabs");
  }, [immersive]);

  if (immersive) return null;

  // Overflow = every role-appropriate rail destination not on a primary tab.
  // Driven by menu's getMenuItemsForRole(role) result (passed as `items`), so
  // the flag-gated mentorships entry and all admin/tutor surfaces are preserved.
  const overflowItems = items.filter((it) => !PRIMARY_ROUTES.has(it.route));

  const activeKey =
    PRIMARY_TABS.find((t) => matchesTab(t, currentRoute))?.key ||
    (moreOpen || overflowItems.some((it) => matchesOverflow(it.route, currentRoute))
      ? "more"
      : null);

  const closeMore = () => setMoreOpen(false);

  const handleSelect = (route) => {
    closeMore();
    if (onSelect) onSelect(route);
  };

  const renderTab = ({ key, label, route, Icon }) => {
    const active = activeKey === key;
    return (
      <button
        key={key}
        type="button"
        data-testid={`tab-${key}`}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className={clsx(classes.tab, { [classes.tabActive]: active })}
        onClick={() => handleSelect(route)}
      >
        <Icon fontSize="small" aria-hidden="true" />
        <span className={classes.label}>{label}</span>
      </button>
    );
  };

  return (
    <>
      <nav
        className={classes.bar}
        data-testid="bottom-tab-bar"
        aria-label="Primary"
      >
        {PRIMARY_TABS.map(renderTab)}
        <button
          type="button"
          data-testid="tab-more"
          aria-label="More"
          aria-haspopup="true"
          aria-expanded={moreOpen}
          aria-current={activeKey === "more" ? "page" : undefined}
          className={clsx(classes.tab, {
            [classes.tabActive]: activeKey === "more",
          })}
          onClick={() => setMoreOpen(true)}
        >
          <MoreHorizOutlined fontSize="small" aria-hidden="true" />
          <span className={classes.label}>More</span>
        </button>
      </nav>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={closeMore}
        PaperProps={{
          className: classes.sheetPaper,
          "data-testid": "more-sheet",
        }}
      >
        <div className={classes.grabber} aria-hidden="true" />
        <List aria-label="More destinations">
          {overflowItems.map((it) => (
            <ListItem
              button
              key={it.route}
              className={classes.sheetRow}
              data-testid={`more-item-${it.route}`}
              onClick={() => handleSelect(it.route)}
            >
              <ListItemIcon className={classes.sheetIcon}>
                {it.icon()}
              </ListItemIcon>
              <ListItemText
                primary={toSentenceCase(it.name)}
                className={classes.sheetText}
              />
            </ListItem>
          ))}
          <Divider />
          <ListItem
            button
            className={classes.sheetRow}
            data-testid="more-theme"
            onClick={() => {
              if (onThemeChange) onThemeChange();
            }}
          >
            <ListItemIcon className={classes.sheetIcon}>
              {currentTheme === "light" ? <Brightness2 /> : <Brightness4 />}
            </ListItemIcon>
            <ListItemText
              primary={currentTheme === "light" ? "Dark theme" : "Light theme"}
              className={classes.sheetText}
            />
          </ListItem>
          <ListItem
            button
            className={classes.sheetRow}
            data-testid="more-logout"
            onClick={() => handleSelect("logout")}
          >
            <ListItemIcon className={classes.sheetIcon}>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary="Sign out" className={classes.sheetText} />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
